/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

import {useEffect, useLayoutEffect, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { TABLES } from "../../../../Apis/Tables";
import { SEARCH_MODEL } from "../../../../components/table/Model/TableModel";
import TableFunction from "../../../../components/table/TableFunction";
import { VIEW_TYPE } from "../../../../enums/ViewType";
import HandleChange from "../../../../Helpers/HandleChange";
import { GetAllMealtimes } from "../../../../reduxToolkit/FoodieOrders/FoodieOrdersApi";
import { SimpleSearch } from "../../../../reduxToolkit/Search/SearchApi";
import routes from "../../../../Routes/routes";

/**
 * Hook for handling the logic behind coffee shop selection in the meal ordering process.
 * Manages cafeteria list, search, selection, and mealtime translations.
 */

const SelectCoffeeShopApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    let { state } = location; // Get state from location
    const servingDate = state?.servingDate; // Get state from location
    const mealTimeId = state?.mealTimeId; // Get state from location
    // Context hook to access meal and its updater from parent route
    const { meal, setMeal } = useOutletContext();
    const { handleChange } = HandleChange({ model: meal, setModel: setMeal })

    // Redux selectors to get global state
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { mealtimes } = useSelector((state) => state.FoodieOrdersSlice);
    const { getFirstFoodieSetting: foodieSetting } = useSelector((state) => state.employeeFoodieSlice);

    // Local state management
    const [showModal, setShowModal] = useState(false);
    const [cafeterias, setCafeterias] = useState({ content: [], totalElements: 0 });
    const [searchElement, setSearchElement] = useState('');
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [search, setSearch] = useState(false);
    const [modified, setModified] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    const contractorId = sessionStorage.getItem('contractorId');
    const supplierId = sessionStorage.getItem('providerId');
    const option = TABLES.FOODIE_CAFETERIA_ORDERS;
    const searchId = 'txt_scs_search_cafeteria';
    const elementRef = useRef(null);

    useLayoutEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect(); // Get the element's position on the screen
            const distanceTop = rect.top + 0; // Calculate the top position of the element
            elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
        }
    }, [cafeterias]);

    /**
     * Sets the cafeteria data into the meal context
     * @param {Object} cafeteria - Cafeteria object selected by the user
     */
    const getCafeteriaData = (cafeteria) => {
        setMeal((prev) => ({
            ...prev,
            cafeteriaId: cafeteria.id,
            cafeteriaName: cafeteria.name,
            latitude: cafeteria.latitude,
            longitude: cafeteria.longitude,
            openTime: cafeteria.schedule.openTime,
            closeTime: cafeteria.schedule.closeTime,
        }));
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page: page,
            size: rowsPerPage,
            model: option,
            key: searchElement
        });

        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true && data && Array.isArray(data.content)) {
                setCafeterias(data);
            } else {
                setCafeterias({ content: [], totalElements: 0 });
            }

        });
    }


    const {
        pagination,
        page,
        setPage,
        rowsPerPage,
    } = TableFunction({
        labelRowsPerPage: 'cafeterias_per_page',
        data: cafeterias,
        hasContainer: true, // Enable container for the table
        fixedPagination: false, // Disable fixed pagination
        view: VIEW_TYPE.GRID.name,
        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
    });


    useEffect(() => {
        if(servingDate) setMeal(prev => ({...prev, date: servingDate }));
    }, [servingDate]);

    useEffect(() => {
        if(mealTimeId) setMeal(prev => ({...prev, mealTime: mealTimeId }));
    }, [mealTimeId]);

    const handleConfirm = () => {
        setEmptyFields(!meal.isValidMealTimeModal())
        if (!meal.isValidMealTimeModal()) return
        navigate(routes.foodieOrders.listMeals);
        setShowModal(false);
    };

    const handleOpen = () => {
        setShowModal(true);
    };

    useEffect(() => {
        dispatch(GetAllMealtimes());
    }, [dispatch]);


    useEffect(() => {
        if (meal.cafeteriaId !== "" && location === routes.foodieOrders.main) {
            setMeal((prevData) => ({
                ...prevData,
                date: "",
                mealTime: ""
            }));
        }
    }, [showModal, meal.cafeteriaId, setMeal, location]);

    useEffect(() => {
        const selectedCafeteria = cafeterias?.content.find(c => c.id === meal.cafeteriaId);
        if (selectedCafeteria) {
            getCafeteriaData(selectedCafeteria);
        }
    }, [meal.cafeteriaId, cafeterias?.content]);



    return {
        DEV,
        permission,
        showModal,
        setShowModal,
        cafeterias,
        setCafeterias,
        handleConfirm,
        handleOpen,
        mealtimes,
        meal,
        setMeal,
        searchId,
        simpleSearch,
        setSimpleSearch,
        searchElement,
        setSearchElement,
        option,
        setSearch,
        supplierId,
        contractorId,
        handleChange,
        pagination,
        page,
        setPage,
        rowsPerPage,
        emptyFields,
        getCafeteriaData,
        elementRef,
        foodieSetting,
        state,
        servingDate,
        mealTimeId
    };
};

export default SelectCoffeeShopApp;
